import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { History, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ICON_COLOR = (a) => {
    if (/sign-off|complete|approved|finalised/i.test(a)) return "bg-emerald-500";
    if (/query/i.test(a)) return "bg-sky-500";
    if (/risk/i.test(a)) return "bg-rose-500";
    if (/reopen|unticked|back|not applicable/i.test(a)) return "bg-amber-500";
    if (/trial balance/i.test(a)) return "bg-indigo-500";
    if (/archiv/i.test(a)) return "bg-slate-500";
    return "bg-brand";
};

export default function ActivityLog() {
    const { user } = useAuth();
    const { engagementId } = useParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/engagements/${engagementId}/activity`).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, [engagementId]);

    return (
        <div className="p-8 max-w-4xl" data-testid="activity-page">
            <div className="flex items-center gap-2 mb-1">
                <History className="w-4 h-4 text-brand" />
                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-display">Audit trail</div>
            </div>
            <h1 className="font-display font-bold text-3xl tracking-tight mb-6">File activity</h1>

            {loading ? <div className="text-muted-foreground text-sm">Loading…</div> : items.length === 0 ? (
                <div className="bg-white border border-border rounded-sm p-8 text-center text-muted-foreground italic" data-testid="activity-empty">No activity recorded on this file yet.</div>
            ) : (
                <div className="bg-white border border-border rounded-sm p-5" data-testid="activity-rows">
                    <ol className="relative border-l border-border ml-2">
                        {items.map((a) => (
                            <li key={a.id} className="ml-5 pb-5 last:pb-0" data-testid="activity-item">
                                <span className={`absolute -left-[7px] mt-1 w-3.5 h-3.5 rounded-full border-2 border-white ${ICON_COLOR(a.action)}`} />
                                <div className="flex items-baseline gap-2 flex-wrap">
                                    <span className="font-medium text-sm">{a.action}</span>
                                    {a.wp_code && (
                                        <Link to={`/engagements/${engagementId}/wp/${a.wp_id}`} className="inline-flex items-center gap-0.5 text-[11px] text-brand font-num hover:underline"><FileText className="w-3 h-3" />{a.wp_code}</Link>
                                    )}
                                    <span className="ml-auto text-[11px] text-muted-foreground font-num">{fmt(a.at)}</span>
                                </div>
                                {a.detail && <div className="text-xs text-muted-foreground mt-0.5">{a.detail}</div>}
                                <div className="text-[11px] text-muted-foreground mt-0.5">{a.actor_name}{user?.role === "Partner" && a.actor_role ? <> · <span className="uppercase tracking-wider">{a.actor_role}</span></> : null}</div>
                            </li>
                        ))}
                    </ol>
                </div>
            )}
        </div>
    );
}

function fmt(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso.slice(0, 16).replace("T", " "); }
}
